import type { ColumnSort, Table, ExpandedState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
} from '@tanstack/react-table';
import React from 'react';
import type {
  ColumnFilters,
  Columns,
  Data,
  TableStateProviderProps,
} from '../types';
import { useQueryParams } from '../../../providers/QueryParamsProvider';
import {
  deriveColumnVisibility,
  prepColumnFilters,
  tanstackColumnAdaptor,
} from '../utils';

export const TableStateContext = React.createContext<{
  table: Table<unknown>;
  data: Data;
  isFetching: boolean;
  refetch: () => void;
  tableId: string;
  columnFilters: ColumnFilters;
  showHeader?: boolean;
  getRowId: (row: any) => string;
} | null>(null);

export const TableStateProvider = (props: TableStateProviderProps) => {
  const queryParams = useQueryParams();
  const columns = (tanstackColumnAdaptor(props.depColumns) ??
    props.columns) as unknown as Columns;
  const [sorting, setSorting] = React.useState<ColumnSort[]>([
    {
      id: queryParams.query.orderBy,
      desc: queryParams.query.order === 'desc',
    },
  ]);
  const [columnVisibility, setColumnVisibility] = React.useState(() =>
    deriveColumnVisibility(columns)
  );
  const [rowSelection, setRowSelection] = React.useState({});

  const columnFilters = prepColumnFilters(columns);

  const table = useReactTable({
    data: props.data?.content ?? [],
    columns,
    ...(props.meta && { meta: props.meta }),
    getCoreRowModel: getCoreRowModel(),
    ...(props.expanded !== undefined && {
      getExpandedRowModel: getExpandedRowModel(),
      onExpandedChange: props.onExpandedChange,
      getRowCanExpand: props.getRowCanExpand,
    }),
    columnResizeMode: 'onEnd',
    columnResizeDirection: 'ltr',
    onSortingChange: setSorting,
    enableRowSelection: true, // enable row selection for all rows
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: (row) => props.getRowId(row),
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      ...(props.expanded !== undefined && { expanded: props.expanded }),
    },
    initialState: {
      columnPinning: {
        left: [],
        right: [],
      },
    },
    defaultColumn: {
      size: 200, //starting column size
      minSize: 100, //enforced during column resizing
      maxSize: 5000, //enforced during column resizing
    },
  });

  const tableState = React.useMemo(() => {
    return {
      table,
      data: props.data,
      isFetching: props.isFetching,
      refetch: props.refetch,
      tableId: props.tableId,
      showHeader: props.showHeader,
      getRowId: props.getRowId,
      columnFilters,
    };
  }, [
    columnFilters,
    props.data,
    props.isFetching,
    props.refetch,
    props.tableId,
    props.showHeader,
    props.getRowId,
    table,
  ]);

  return (
    <TableStateContext.Provider value={tableState}>
      {props.children}
    </TableStateContext.Provider>
  );
};

export const useTableState = () => {
  const context = React.useContext(TableStateContext);

  if (!context) {
    throw new Error('useTableState must be used within a TableStateProvider');
  }

  return context;
};
